/**
 * Scan modes for the Options Scalper.
 *
 * The backend has no concept of a "mode" -- its API takes two independent
 * booleans, `use_price_filter` and `use_drop_filter`, exactly as the spec's
 * filter-composition truth table defines them. These four named modes are a UI
 * affordance layered on top, so the user picks the QUESTION they're asking
 * rather than assembling flag combinations by hand.
 *
 * Keeping the mapping in one place means modes can be renamed, reordered or
 * added without touching the endpoint or the screening logic.
 */

export const SCAN_MODES = [
  {
    key: 'cheap',
    label: 'Cheap contracts',
    icon: '💵',
    question: 'What can I buy under a set price? Yesterday\'s close is not considered at all.',
    usePriceFilter: true,
    useDropFilter: false,
  },
  {
    key: 'discounted',
    label: 'Discounted contracts',
    icon: '📉',
    question: 'What has fallen hard since yesterday\'s close, at any price?',
    usePriceFilter: false,
    useDropFilter: true,
  },
  {
    key: 'both',
    label: 'Cheap AND discounted',
    icon: '🎯',
    question: 'Cheap in absolute dollars and marked down hard — the primary setup.',
    usePriceFilter: true,
    useDropFilter: true,
  },
  {
    key: 'browse',
    label: 'Browse all',
    icon: '🔎',
    question: 'No price or discount gate — just expiration and moneyness. A raw chain browser.',
    usePriceFilter: false,
    useDropFilter: false,
  },
];

export const DEFAULT_MODE = 'both';

export function getMode(key) {
  return SCAN_MODES.find((m) => m.key === key) || SCAN_MODES.find((m) => m.key === DEFAULT_MODE);
}

/** Map a mode key onto the two booleans the API actually accepts. */
export function modeToFlags(key) {
  const mode = getMode(key);
  return {
    use_price_filter: mode.usePriceFilter,
    use_drop_filter: mode.useDropFilter,
  };
}

export const EXPIRATION_BUCKETS = [
  { key: 'lt1m', label: '< 1 month' },
  { key: '1-3m', label: '1-3 months' },
  { key: '3-5m', label: '3-5 months' },
  { key: '5-8m', label: '5-8 months' },
  { key: '8-12m', label: '8-12 months' },
  { key: '12m+', label: '12m+ (LEAPS)' },
];

export const SORT_OPTIONS = [
  { key: 'drop_percent', label: 'Biggest drop' },
  { key: 'last_price', label: 'Cheapest first' },
  { key: 'days_to_expiration', label: 'Soonest expiration' },
  { key: 'otm_percent', label: 'Closest to the money' },
  { key: 'volume', label: 'Most volume' },
];
